import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark';
export type Density = 'comfortable' | 'compact';

// Dashboard period-comparison mode, set from the command palette ("vs last
// week" / "vs last month"). Metric widgets show a green/red delta chip
// comparing the current period against the one before it.
export type DashboardComparisonMode = 'none' | 'last_week' | 'last_month';

export interface ObjectPanelContext {
  type: 'objectType' | 'pipeline' | 'agent' | 'connector';
  id: string;
  label?: string;
}

interface UiStore {
  theme: Theme;
  density: Density;
  navCollapsed: boolean;
  commandPaletteOpen: boolean;
  shortcutsOverlayOpen: boolean;
  activeObjectPanel: ObjectPanelContext | null;
  widgetSelectionMode: boolean;
  selectedWidgetIds: string[];
  dashboardComparison: DashboardComparisonMode;

  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
  setNavCollapsed: (v: boolean) => void;
  toggleTheme: () => void;
  toggleDensity: () => void;
  toggleNav: () => void;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  openShortcutsOverlay: () => void;
  closeShortcutsOverlay: () => void;
  openObjectPanel: (ctx: ObjectPanelContext) => void;
  closeObjectPanel: () => void;
  enterWidgetSelection: () => void;
  exitWidgetSelection: () => void;
  toggleWidgetSelected: (id: string) => void;
  setDashboardComparison: (m: DashboardComparisonMode) => void;
}

export const useUiStore = create<UiStore>()(
  persist(
    (set, get) => ({
      theme: 'light',
      density: 'comfortable',
      navCollapsed: false,
      commandPaletteOpen: false,
      shortcutsOverlayOpen: false,
      activeObjectPanel: null,
      widgetSelectionMode: false,
      selectedWidgetIds: [],
      dashboardComparison: 'none',

      setTheme: (t) => set({ theme: t }),
      setDensity: (d) => set({ density: d }),
      setNavCollapsed: (v) => set({ navCollapsed: v }),
      toggleTheme: () => set((s) => ({ theme: s.theme === 'light' ? 'dark' : 'light' })),
      toggleDensity: () => set((s) => ({ density: s.density === 'comfortable' ? 'compact' : 'comfortable' })),
      toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),

      openCommandPalette: () => set({ commandPaletteOpen: true }),
      closeCommandPalette: () => set({ commandPaletteOpen: false }),

      openShortcutsOverlay: () => set({ shortcutsOverlayOpen: true }),
      closeShortcutsOverlay: () => set({ shortcutsOverlayOpen: false }),

      openObjectPanel: (ctx) => set({ activeObjectPanel: ctx }),
      closeObjectPanel: () => set({ activeObjectPanel: null }),

      enterWidgetSelection: () => set({ widgetSelectionMode: true, selectedWidgetIds: [], commandPaletteOpen: false }),
      exitWidgetSelection: () => set({ widgetSelectionMode: false, selectedWidgetIds: [] }),
      setDashboardComparison: (m) => set({ dashboardComparison: m, commandPaletteOpen: false }),
      toggleWidgetSelected: (id) =>
        set((s) => ({
          selectedWidgetIds: s.selectedWidgetIds.includes(id)
            ? s.selectedWidgetIds.filter((x) => x !== id)
            : [...s.selectedWidgetIds, id],
        })),
    }),
    {
      name: 'nexus-ui-prefs',
      partialize: (state) => ({ theme: state.theme, density: state.density, navCollapsed: state.navCollapsed }),
    },
  ),
);
